/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useVendorVerification } from '../../hooks/useVendorVerification';
import useVendorPlanAccess from '../../hooks/useVendorPlanAccess';
import VendorVerificationModal from '../vendor/VendorVerificationModal';
import { useUnreadMessagesCount } from '../../services/messageService';
import { useUnreadNotificationsCount, useUnreadSupportTicketCount } from '../../services/notificationService';
import { 
  LayoutDashboard, 
  User, 
  Calendar, 
  Image, 
  BarChart3, 
  Settings,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CreditCard,
  DollarSign,
  Star,
  Package,
  MapPin,
  Clock,
  CheckCircle,
  Crown,
  Eye,
  Download,
  Upload,
  HelpCircle,
  Zap,
  Target,
  BookOpen,
  FileCheck,
  Bell,
  Shield,
  TrendingUp,
  Lock,
  Camera,
  Video,
  Gift,
  Heart,
  Users,
  UserCheck,
  FileText,
  FileSignature,
  Wallet,
  Info,
  Share,
  LogOut,
  Palette,
  Headphones
} from 'lucide-react';

/** Match nav href to pathname without false positives (e.g. /vendor/support vs /vendor/support-tickets). */
function navPathMatches(href, pathname) {
  if (!href || !pathname) return false;
  if (href === '/vendor') {
    return pathname === '/vendor' || pathname === '/vendor/';
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

const VendorSidebar = () => {
  const location = useLocation();
  const { 
    isVendorSidebarCollapsed, 
    toggleVendorSidebar, 
    isVendorMobileSidebarOpen, 
    isVendorMobileSidebarCollapsed,
    toggleVendorMobileSidebar,
    closeVendorMobileSidebar 
  } = useSidebar();
  const { user, logout } = useAuth();
  const { canAccessPath, isFreeTier } = useVendorPlanAccess();
  const { 
    isVendorVerified, 
    isVerificationPending,
    showVerificationModal, 
    showVerificationRequired, 
    hideVerificationModal,
    isLoading,
    verificationStatus
  } = useVendorVerification();

  // Get unread counts for badges
  const { data: unreadMessagesData } = useUnreadMessagesCount();
  const unreadMessagesCount = unreadMessagesData?.data || 0;

  const { data: unreadNotificationsData } = useUnreadNotificationsCount();
  const unreadNotificationsCount = unreadNotificationsData?.data ?? unreadNotificationsData ?? 0;

  const { data: unreadSupportTicketsData } = useUnreadSupportTicketCount();
  const unreadSupportTicketsCount = unreadSupportTicketsData ?? 0;

  const [openDropdowns, setOpenDropdowns] = useState({ 'My Plan': true });

  const toggleDropdown = (dropdownName) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [dropdownName]: !prev[dropdownName]
    }));
  };

  const handleLogout = async () => {
    await logout();
  };

  // Debug logging
  // console.log('VendorSidebar: Current state:', {
  //   isVendorVerified,
  //   isVerificationPending,
  //   showVerificationModal,
  //   isLoading,
  //   verificationStatus: verificationStatus?.length || 0
  // });
  
  const navigationGroups = [
    // Main Dashboard
    {
      type: 'single',
      items: [
        { name: 'Dashboard', href: '/vendor', icon: LayoutDashboard, requiresVerification: false }
      ]
    },
    
    // My Plan - subscription features
    {
      type: 'dropdown',
      name: 'My Plan',
      icon: Crown,
      items: [
        { name: 'Plans & pricing', href: '/vendor/subscription', icon: CreditCard, requiresVerification: false },
        { name: 'Advanced Analytics', href: '/vendor/advanced-analytics', icon: BarChart3, requiresVerification: true },
        { name: 'Custom Branding', href: '/vendor/branding', icon: Palette, requiresVerification: true },
        { name: 'Priority Support', href: '/vendor/support-tickets', icon: Headphones, requiresVerification: true },
        { name: 'Advanced Booking', href: '/vendor/advanced-bookings', icon: Calendar, requiresVerification: true },
        { name: 'Multi-Location', href: '/vendor/locations', icon: MapPin, requiresVerification: true }
      ]
    },
    
    // Profile & Business
    {
      type: 'dropdown',
      name: 'Profile & Business',
      icon: User,
      items: [
        { name: 'Profile', href: '/vendor/profile', icon: User, requiresVerification: true },
        // { name: 'Service Areas', href: '/vendor/service-areas', icon: MapPin, requiresVerification: true },
        // { name: 'Availability', href: '/vendor/availability', icon: Clock, requiresVerification: true }
      ]
    },
    
    // Services & Packages
    {
      type: 'dropdown',
      name: 'Services',
      icon: Package,
      items: [
        { name: 'Services', href: '/vendor/services', icon: Package, requiresVerification: true },
        { name: 'Packages', href: '/vendor/packages', icon: Gift, requiresVerification: true },
        // { name: 'Pricing', href: '/vendor/pricing', icon: DollarSign, requiresVerification: true },
        // { name: 'Equipment', href: '/vendor/equipment', icon: Package, requiresVerification: true }
      ]
    },
    // Bookings & Calendar
    {
      type: 'dropdown',
      name: 'Bookings',
      icon: Calendar,
      items: [
        { name: 'Bookings', href: '/vendor/bookings', icon: Calendar, requiresVerification: true },
        { name: 'Private Bookings', href: '/vendor/private-bookings', icon: Lock, requiresVerification: true },
        { name: 'Consultations', href: '/vendor/consultations', icon: Video, requiresVerification: true },
        { name: 'Calendar', href: '/vendor/calendar', icon: Calendar, requiresVerification: true },
        // { name: 'Booking Requests', href: '/vendor/requests', icon: CheckCircle, requiresVerification: true },
        // { name: 'Booking History', href: '/vendor/history', icon: Clock, requiresVerification: true }
      ]
    },
    
    // // Portfolio & Media
    {
      type: 'dropdown',
      name: 'Portfolio & Media',
      icon: Image,
      items: [
        { name: 'Portfolio', href: '/vendor/portfolio', icon: Image, requiresVerification: true },
        { name: 'Photo Gallery', href: '/vendor/gallery', icon: Camera, requiresVerification: true },
        // { name: 'Video Gallery', href: '/vendor/videos', icon: Video, requiresVerification: true },
        // { name: 'Media Library', href: '/vendor/media', icon: Image, requiresVerification: true }
      ]
    },
    
    
    
    // Financial
    {
      type: 'dropdown',
      name: 'Financial',
      icon: DollarSign,
      items: [
        { name: 'Earnings', href: '/vendor/earnings', icon: DollarSign, requiresVerification: true },
        { name: 'Payments', href: '/vendor/payments', icon: CreditCard, requiresVerification: true },
        { name: 'Payouts (admin)', href: '/vendor/payout-disbursements', icon: Wallet, requiresVerification: true },
        { name: 'Discount Codes', href: '/vendor/discount-codes', icon: Gift, requiresVerification: true },
        { name: 'Invoices', href: '/vendor/invoices', icon: FileText, requiresVerification: true },
        { name: 'Contracts', href: '/vendor/contracts', icon: FileSignature, requiresVerification: true },
        // { name: 'Tax Reports', href: '/vendor/tax-reports', icon: FileCheck, requiresVerification: true }
      ]
    },
    
    // Analytics & Reports
    {
      type: 'dropdown',
      name: 'Analytics',
      icon: BarChart3,
      items: [
        { name: 'Analytics', href: '/vendor/analytics', icon: BarChart3, requiresVerification: true },
        { name: 'Performance', href: '/vendor/performance', icon: TrendingUp, requiresVerification: true },
        { name: 'Reports', href: '/vendor/reports', icon: FileCheck, requiresVerification: true },
        // { name: 'Insights', href: '/vendor/insights', icon: Eye, requiresVerification: true }
      ]
    },
    
    // Communication
    {
      type: 'dropdown',
      name: 'Communication',
      icon: MessageSquare,
      items: [
        // { name: 'Messages', href: '/vendor/messages', icon: MessageSquare, requiresVerification: true },
        { name: 'Notifications', href: '/vendor/notifications', icon: Bell, requiresVerification: true },
        { name: 'Reviews', href: '/vendor/reviews', icon: Star, requiresVerification: true },
      ]
    },
    
    // Marketing & Promotion
    // {
    //   type: 'dropdown',
    //   name: 'Marketing',
    //   icon: Zap,
    //   items: [
    //     { name: 'Promotions', href: '/vendor/promotions', icon: Zap, requiresVerification: true },
    //     { name: 'Coupons', href: '/vendor/coupons', icon: Gift, requiresVerification: true },
    //     { name: 'Social Media', href: '/vendor/social', icon: Share, requiresVerification: true },
    //     { name: 'Marketing Tools', href: '/vendor/marketing', icon: Target, requiresVerification: true }
    //   ]
    // },
    
    // Team & Staff
    // {
    //   type: 'dropdown',
    //   name: 'Team',
    //   icon: Users,
    //   items: [
    //     { name: 'Team Members', href: '/vendor/team', icon: Users, requiresVerification: true },
    //     { name: 'Staff Management', href: '/vendor/staff', icon: UserCheck, requiresVerification: true },
    //     { name: 'Permissions', href: '/vendor/permissions', icon: Lock, requiresVerification: true },
    //     { name: 'Training', href: '/vendor/training', icon: BookOpen, requiresVerification: true }
    //   ]
    // },
    
    // Tools & Utilities
    // {
    //   type: 'dropdown',
    //   name: 'Tools',
    //   icon: Package,
    //   items: [
    //     { name: 'Tools', href: '/vendor/tools', icon: Package, requiresVerification: true },
    //     { name: 'Backup', href: '/vendor/backup', icon: Download, requiresVerification: true },
    //     { name: 'Export Data', href: '/vendor/export', icon: Download, requiresVerification: true },
    //     { name: 'Import Data', href: '/vendor/import', icon: Upload, requiresVerification: true }
    //   ]
    // },
    
    // Support and Contact
    {
      type: 'dropdown',
      name: 'Support and Contact',
      icon: HelpCircle,
      items: [
        { name: 'Support', href: '/vendor/support', icon: HelpCircle, requiresVerification: false },
        // { name: 'Contact', href: '/contact', icon: MessageSquare, requiresVerification: false }
      ]
    },
    
    // Settings & Configuration
    {
      type: 'dropdown',
      name: 'Settings',
      icon: Settings,
      items: [
        // { name: 'Settings', href: '/vendor/settings', icon: Settings, requiresVerification: false },
        { name: 'Security', href: '/vendor/security', icon: Shield, requiresVerification: false },
        { name: 'Account', href: '/vendor/account', icon: User, requiresVerification: false },
        { name: 'Preferences', href: '/vendor/preferences', icon: Settings, requiresVerification: false }
      ]
    }
  ];

  // Show all navigation groups (no subscription filtering)
  const getNavigationGroups = () => {
    return navigationGroups
      .map((group) => {
        if (group.type === 'single') {
          return {
            ...group,
            items: group.items.filter((item) => canAccessPath(item.href)),
          };
        }
        if (group.type === 'dropdown') {
          return {
            ...group,
            items: group.items.filter((item) => canAccessPath(item.href)),
          };
        }
        return group;
      })
      .filter((group) => (group.items?.length ?? 0) > 0);
  };

  // Keep the section that owns the current route open (reload + in-app navigation)
  useEffect(() => {
    const pathname = location.pathname;
    setOpenDropdowns((prev) => {
      const next = { ...prev };
      let changed = false;
      navigationGroups.forEach((group) => {
        if (group.type !== 'dropdown') return;
        const hasActiveChild = group.items.some((item) =>
          navPathMatches(item.href, pathname)
        );
        if (hasActiveChild && !next[group.name]) {
          next[group.name] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [location.pathname]);

  // Handle navigation click
  const handleNavigationClick = (item, e) => {
    // console.log('VendorSidebar: Navigation click:', {
    //   itemName: item.name,
    //   requiresVerification: item.requiresVerification,
    //   isVendorVerified,
    //   isVerificationPending,
    //   showVerificationModal
    // });
    
    if (item.requiresVerification && !isVendorVerified) {
      e.preventDefault();
      console.log('VendorSidebar: Showing verification modal');
      showVerificationRequired();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isVendorMobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeVendorMobileSidebar}
        />
      )}
      
      {/* Sidebar */}
      <div className={`lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:pt-16 lg:z-40 transition-all duration-300 ${
        isVendorSidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
      } ${
        isVendorMobileSidebarOpen 
          ? `fixed inset-y-0 left-0 z-50 pt-16 flex flex-col ${
              isVendorMobileSidebarCollapsed ? 'w-16' : 'w-64'
            }` 
          : 'hidden'
      }`}>
      <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transition-colors">
        {/* Profile Section */}
        <div className="px-4 py-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                <span className="text-white font-medium text-sm">
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'V'}
                </span>
              </div>
            </div>
            {!isVendorSidebarCollapsed && !isVendorMobileSidebarCollapsed && (
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-normal text-gray-900 dark:text-gray-100 truncate">
                  {user?.name || 'Vendor'}
                </p>
                <p className="text-xs font-light text-gray-500 dark:text-gray-400 truncate">
                  {user?.email || 'vendor@example.com'}
                </p>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-light bg-green-100 text-green-800">
                    Active
                  </span>
                  {isFreeTier && (
                    <>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-900">
                        Free plan
                      </span>
                      <Link
                        to="/vendor/subscription"
                        className="text-xs font-medium text-primary-600 hover:text-primary-800 underline underline-offset-2"
                        onClick={() => closeVendorMobileSidebar()}
                      >
                        Upgrade
                      </Link>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Collapse Toggle */}
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-800">
          <button
            onClick={() => {
              // Use mobile toggle on mobile, desktop toggle on desktop
              if (window.innerWidth < 1024) {
                toggleVendorMobileSidebar();
              } else {
                toggleVendorSidebar();
              }
            }}
            className="w-full flex items-center justify-center p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md transition-colors"
          >
            {(isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed) ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
          {getNavigationGroups().map((group, groupIndex) => {
            if (group.type === 'single') {
              return group.items.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={(e) => handleNavigationClick(item, e)}
                  className={({ isActive }) =>
                    `group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-primary-100 text-primary-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    } ${
                      item.requiresVerification && !isVendorVerified 
                        ? isVerificationPending 
                          ? 'opacity-75 cursor-default' 
                          : 'opacity-60 cursor-not-allowed'
                        : ''
                    }`
                  }
                  title={(isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed) ? item.name : undefined}
                >
                  <item.icon className={`h-5 w-5 ${(isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed) ? '' : 'mr-3'}`} />
                  {!(isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed) && item.name}
                </NavLink>
              ));
            }

            if (group.type === 'dropdown') {
              const hasActiveChild = group.items.some((item) =>
                navPathMatches(item.href, location.pathname)
              );
              // Active section always stays expanded so reload / deep links keep context
              const isDropdownOpen = hasActiveChild || !!openDropdowns[group.name];
              const isCollapsed = isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed;
              
              return (
                <div key={group.name} className="space-y-1">
                  {/* Dropdown Header */}
                  <button
                    type="button"
                    onClick={() => {
                      if (hasActiveChild) return;
                      toggleDropdown(group.name);
                    }}
                    className={`group w-full flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                      isDropdownOpen || hasActiveChild
                        ? 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                    }`}
                    title={isCollapsed ? group.name : undefined}
                    aria-expanded={isDropdownOpen}
                  >
                    <group.icon className={`h-5 w-5 ${isCollapsed ? '' : 'mr-3'}`} />
                    {!isCollapsed && (
                      <>
                        <span className="flex-1 text-left">{group.name}</span>
                        <ChevronDown 
                          className={`h-4 w-4 transition-transform ${
                            isDropdownOpen ? 'rotate-180' : ''
                          }`} 
                        />
                      </>
                    )}
                  </button>
                  
                  {/* Dropdown Items */}
                  {!isCollapsed && isDropdownOpen && (
                    <div className="ml-6 space-y-1">
                      {group.items.map((item) => {
                        // Get badge count for Messages, Notifications, and Priority Support
                        const badgeCount = item.name === 'Messages' 
                          ? unreadMessagesCount 
                          : item.name === 'Notifications' 
                          ? unreadNotificationsCount 
                          : item.name === 'Priority Support'
                          ? unreadSupportTicketsCount
                          : 0;

                        return (
                          <NavLink
                            key={item.name}
                            to={item.href}
                            onClick={(e) => handleNavigationClick(item, e)}
                            className={({ isActive }) =>
                              `group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                                isActive
                                  ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/40 dark:text-primary-200'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                              } ${
                                item.requiresVerification && !isVendorVerified 
                                  ? isVerificationPending 
                                    ? 'opacity-75 cursor-default' 
                                    : 'opacity-60 cursor-not-allowed'
                                  : ''
                              }`
                            }
                          >
                            <div className="flex items-center">
                              <item.icon className="h-4 w-4 mr-3" />
                              {item.name}
                            </div>
                            {badgeCount > 0 && (
                              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-primary-600 text-white rounded-full min-w-[1.25rem] text-center">
                                {badgeCount > 99 ? '99+' : badgeCount}
                              </span>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            return null;
          })}
        </nav>

        {/* Logout Section */}
        <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-2 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/50 rounded-md transition-colors group"
            title={(isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed) ? 'Logout' : undefined}
          >
            <LogOut className={`h-5 w-5 ${(isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed) ? '' : 'mr-3'}`} />
            {!(isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed) && 'Logout'}
          </button>
        </div>
      </div>
      </div>
      
      {/* Verification Modal */}
      <VendorVerificationModal
        isOpen={showVerificationModal}
        onClose={hideVerificationModal}
        isPending={isVerificationPending}
      />
    </>
  );
};

export default VendorSidebar;
